/* eslint-disable tsdoc/syntax */
import { Step, StepLabel } from "@mui/material";

import { StepperIconComponent } from "@zenith/components";
import {
  BusinessDetailsPage,
  DocumentVerificationPage,
  MobileOnlyStepPage,
  NextOfKinDetailsPage,
  NinVerificationPage,
  ProofOfAddressPage,
  RegistrationDetailsPage,
} from "@zenith/pages";
import { TierStepsResponseDTO } from "@zenith/services/user";
import { VerificationSteps } from "./enums";

const stepElement = (
  label: string,
  stepStatus?: TierStepsResponseDTO.stepStatus
) => {
  const color = getStepColor(stepStatus);

  return (
    <Step key={label}>
      <StepLabel
        StepIconComponent={(props) =>
          StepperIconComponent({ ...props, stepStatus })
        }
        sx={{
          "& .MuiStepLabel-label.Mui-completed": {
            color: `${color} !important`,
          },
          "& .MuiStepLabel-label.Mui-disabled": {
            color: `${color} !important`,
          },
        }}
      >
        {label}
      </StepLabel>
    </Step>
  );
};

/**
 * Silver tier
 */
export const outletForTierSilver = (
  activeStep: number,
  tier: string,
  steps: Array<TierStepsResponseDTO>
): JSX.Element => {
  switch (activeStep) {
    case 0:
      return <NinVerificationPage step={steps[activeStep]} />;
    case 1:
      return <NextOfKinDetailsPage step={steps[activeStep]} />;
    default:
      throw new Error("Wrong active step");
  }
};

export const stepTierSilver = (steps: Array<TierStepsResponseDTO>) => {
  return steps.map((step) => {
    const stepName = step.stepName || VerificationSteps.START_PAGE_TIERS_STEP;
    return stepElement(VerificationSteps[stepName], step.stepStatus);
  });
};

/**
 * Gold tier
 */
export const outletForTierGold = (
  activeStep: number,
  tier: string,
  steps: Array<TierStepsResponseDTO>
): JSX.Element => {
  const mobileDeepLink = import.meta.env.VITE_MOBILE_DEEP_LINK as string;
  switch (activeStep) {
    case 0:
      return <NinVerificationPage step={steps[activeStep]} />;
    case 1:
      return <NextOfKinDetailsPage step={steps[activeStep]} />;
    case 2:
      return <DocumentVerificationPage step={steps[activeStep]} />;
    case 3:
      return (
        <MobileOnlyStepPage
          step={steps[activeStep]}
          mobileDeepLink={mobileDeepLink}
        />
      );
    case 4:
      return <ProofOfAddressPage step={steps[activeStep]} />;
    default:
      throw new Error("Wrong active step");
  }
};

export const stepTierGold = (
  steps: Array<TierStepsResponseDTO>
): JSX.Element[] => {
  return steps.map((step) => {
    const stepName = step.stepName || VerificationSteps.START_PAGE_TIERS_STEP;
    return stepElement(VerificationSteps[stepName], step.stepStatus);
  });
};

export const outletForTierPlatinum = (
  activeStep: number,
  tier: string,
  steps: Array<TierStepsResponseDTO>
): JSX.Element => {
  const mobileDeepLink = import.meta.env.VITE_MOBILE_DEEP_LINK as string;

  switch (activeStep) {
    case 0:
      return <NinVerificationPage step={steps[activeStep]} />;
    case 1:
      return <NextOfKinDetailsPage step={steps[activeStep]} />;
    case 2:
      return <DocumentVerificationPage step={steps[activeStep]} />;
    case 3:
      return (
        <MobileOnlyStepPage
          step={steps[activeStep]}
          mobileDeepLink={mobileDeepLink}
        />
      );
    case 4:
      return <ProofOfAddressPage step={steps[activeStep]} />;
    case 5:
      return <RegistrationDetailsPage step={steps[activeStep]} />;
    case 6:
      return <BusinessDetailsPage step={steps[activeStep]} tier={tier} />;
    default:
      throw new Error("Wrong active step");
  }
};

export const stepTierPlatinum = (steps: Array<TierStepsResponseDTO>) => {
  return steps.map((step) => {
    const stepName = step.stepName || VerificationSteps.START_PAGE_TIERS_STEP;
    return stepElement(VerificationSteps[stepName], step.stepStatus);
  });
};

function getStepColor(
  stepStatus?: TierStepsResponseDTO.stepStatus
): "#F0B02A" | "#6EBD7F" | "#DA291C" | "#9B9B9B" {
  const orangeColor = "#F0B02A";
  const greenColor = "#6EBD7F";
  const redColor = "#DA291C";
  const grayColor = "#9B9B9B";

  switch (stepStatus) {
    case TierStepsResponseDTO.stepStatus.APPROVED:
      return greenColor;
    case TierStepsResponseDTO.stepStatus.PENDING:
      return orangeColor;
    case TierStepsResponseDTO.stepStatus.REJECTED:
      return redColor;

    default:
      return grayColor;
  }
}
